import type { Agent, AgentConfig, Avatar, ContextUsage, UpdateAgentRequest } from "@ark-bot/protocol";
import { useEffect, useState } from "react";

import { api, type ModelChoices, type ModelOption } from "../api.js";
import { formatLocale, useText } from "../i18n.js";
import { AGENT_SETTINGS } from "../text/agent-settings.js";

import { AvatarField } from "./AvatarPicker.js";

type Words = (typeof AGENT_SETTINGS)["en"];

interface Props {
  readonly agent: Agent;
  /** How full the agent's context is after its last call, for the gauge under the slider. */
  readonly context?: ContextUsage | null;
  /** A turn is running, so Compact now waits for it to finish. */
  readonly busy?: boolean;
  readonly onSave: (request: UpdateAgentRequest) => Promise<void>;
  readonly onClose: () => void;
}

const thousands = (tokens: number): string => `${Math.round(tokens / 1000)}k`;

/** The context slider's ends and where it starts; the host holds the same numbers (core/context.ts). */
export const CONTEXT_MIN = 25_000;
export const CONTEXT_MAX = 150_000;
export const CONTEXT_DEFAULT = 50_000;

export type Effort = NonNullable<AgentConfig["effort"]>;

/**
 * Blank fields mean "unset", and so do an undefined main model and effort:
 * the bot then runs the default. The form's model starts at the saved one, so
 * a host with no picker carries it over; the cheap lane is not shown here and
 * is always carried over.
 */
export function configOf(title: string, description: string, avatar: Avatar | undefined, notify: boolean, current: AgentConfig, contextTokens: number | undefined, mainModel: string | undefined, effort: Effort | undefined): AgentConfig {
  const config: AgentConfig = {};
  if (title.trim().length > 0) config.title = title.trim();
  if (description.trim().length > 0) config.description = description.trim();
  if (mainModel != null) config.mainModel = mainModel;
  if (effort != null) config.effort = effort;
  if (current.cheapModel != null) config.cheapModel = current.cheapModel;
  if (avatar != null) config.avatar = avatar;
  if (!notify) config.notify = false;
  if (contextTokens != null && contextTokens !== CONTEXT_DEFAULT) config.contextTokens = contextTokens;
  return config;
}

const credits = (amount: number): string => amount.toLocaleString(formatLocale("en-US"), { maximumFractionDigits: 1 });
const priceOf = (t: Words, model: ModelOption): string => (model.input == null || model.output == null ? "" : t.price(credits(model.input), credits(model.output)));

/**
 * Which model the bot runs, from those the operator offers. "Default" leaves
 * it unset, and the bot runs whatever the default is at the time, so a new
 * default reaches it without a visit here. A saved model taken off the offer
 * stays selectable as what it is, so opening the settings never changes it.
 */
export function ModelField({ choices, value, onChange }: { readonly choices: ModelChoices; readonly value: string | undefined; readonly onChange: (next: string | undefined) => void }): React.JSX.Element {
  const t = useText(AGENT_SETTINGS);
  const picked = value == null ? null : (choices.models.find((model) => model.id === value) ?? null);
  const fallback = choices.default == null ? null : (choices.models.find((model) => model.id === choices.default?.id) ?? null);
  const note = value == null ? `${t.runsDefault} ${fallback == null ? "" : priceOf(t, fallback)}`.trim() : picked == null ? t.noLongerOfferedNote : priceOf(t, picked);
  return (
    <label>
      {t.model}
      <select value={value ?? ""} onChange={(event) => onChange(event.target.value === "" ? undefined : event.target.value)} title={t.modelHint}>
        <option value="">{choices.default == null ? t.defaultChoice : t.defaultWith(choices.default.label)}</option>
        {choices.models.map((model) => (
          <option key={model.id} value={model.id}>
            {model.label}
          </option>
        ))}
        {value != null && picked == null ? <option value={value}>{t.noLongerOffered(value.replace(/^api\//, ""))}</option> : null}
      </select>
      <span className="muted model-note">{note}</span>
    </label>
  );
}

const EFFORTS: readonly { readonly value: Effort; readonly label: (t: Words) => string }[] = [
  { value: "minimal", label: (t) => t.effortMinimal },
  { value: "low", label: (t) => t.effortLow },
  { value: "medium", label: (t) => t.effortMedium },
  { value: "high", label: (t) => t.effortHigh },
];

/**
 * How hard the bot's model thinks before it answers. "Default" leaves it
 * unset, and the model thinks as it does by default. A model that cannot take
 * an effort is asked without one, so any choice is safe to save.
 */
export function EffortField({ value, onChange }: { readonly value: Effort | undefined; readonly onChange: (next: Effort | undefined) => void }): React.JSX.Element {
  const t = useText(AGENT_SETTINGS);
  const note = value == null ? t.effortDefaultNote : value === "high" ? t.effortHighNote : value === "medium" ? t.effortMediumNote : t.effortLowNote;
  return (
    <label>
      {t.effort}
      <select value={value ?? ""} onChange={(event) => onChange(event.target.value === "" ? undefined : (event.target.value as Effort))} title={t.effortHint}>
        <option value="">{t.defaultChoice}</option>
        {EFFORTS.map((effort) => (
          <option key={effort.value} value={effort.value}>
            {effort.label(t)}
          </option>
        ))}
      </select>
      <span className="muted model-note">{note}</span>
    </label>
  );
}

/** The prototype's on/off switch, as a checkbox styled the way theirs looks. */
export function Switch({ checked, onChange, label }: { readonly checked: boolean; readonly onChange: (next: boolean) => void; readonly label: string }): React.JSX.Element {
  return (
    <label className="switch">
      <input type="checkbox" role="switch" checked={checked} onChange={(event) => onChange(event.target.checked)} aria-label={label} />
      <span className="switch-track" aria-hidden="true">
        <span className="switch-thumb" />
      </span>
    </label>
  );
}

/**
 * Condenses the conversation now instead of when the window fills: the
 * earlier part becomes a note the agent reads first, the last exchange stays
 * as it was. It acts at once, not on Save, and only while the agent is idle.
 */
function CompactNow({ agentId, busy }: { readonly agentId: string; readonly busy: boolean }): React.JSX.Element {
  const t = useText(AGENT_SETTINGS);
  const [compacting, setCompacting] = useState(false);
  // What came back, worded at render so it follows the app's language.
  const [result, setResult] = useState<{ readonly condensed: number; readonly error?: undefined } | { readonly error: string } | null>(null);

  const compact = async (): Promise<void> => {
    setCompacting(true);
    setResult(null);
    try {
      const { condensedEntries } = await api.compact(agentId);
      setResult({ condensed: condensedEntries });
    } catch (failure) {
      setResult({ error: failure instanceof Error ? failure.message : String(failure) });
    } finally {
      setCompacting(false);
    }
  };

  const resultText = result == null ? null : result.error != null ? result.error : result.condensed === 0 ? t.nothingToCondense : t.condensed(result.condensed);
  return (
    <div className="compact-now">
      <button type="button" onClick={() => void compact()} disabled={compacting || busy} title={busy ? t.compactBusyHint : t.compactHint}>
        {compacting ? t.compacting : t.compactNow}
      </button>
      <span className={result?.error != null ? "compact-error" : "muted"} role="status">
        {compacting ? t.writingNote : (resultText ?? (busy ? t.compactBusy : ""))}
      </span>
    </div>
  );
}

/**
 * The agent's settings, laid out as the prototype's: the face on top,
 * then name, label and the description the model sees, then whether it may
 * notify. A group has no label, and its members are managed from the pane
 * rather than here. The parent keys this on the agent id so switching agents
 * resets the form instead of carrying edits across.
 */
export function AgentSettings({ agent, context = null, busy = false, onSave, onClose }: Props): React.JSX.Element {
  const t = useText(AGENT_SETTINGS);
  const [name, setName] = useState(agent.name);
  const [title, setTitle] = useState(agent.config.title ?? "");
  const [description, setDescription] = useState(agent.config.description ?? "");
  const [avatar, setAvatar] = useState<Avatar | undefined>(agent.config.avatar);
  const [notify, setNotify] = useState(agent.config.notify !== false);
  const [contextTokens, setContextTokens] = useState(agent.config.contextTokens ?? CONTEXT_DEFAULT);
  const [mainModel, setMainModel] = useState(agent.config.mainModel);
  const [effort, setEffort] = useState(agent.config.effort);
  const [choices, setChoices] = useState<ModelChoices | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isGroup = agent.kind === "group";

  // A group has no model of its own; an agent's picker appears once the host says what it offers.
  useEffect(() => {
    if (isGroup) return;
    let live = true;
    void api.models().then((answer) => {
      if (live) setChoices(answer);
    });
    return () => {
      live = false;
    };
  }, [isGroup]);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const trimmed = name.trim();
    if (saving || trimmed.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      await onSave({ name: trimmed, config: configOf(title, description, avatar, notify, agent.config, isGroup ? undefined : contextTokens, mainModel, isGroup ? undefined : effort) });
      onClose();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="agent-settings profile-form" onSubmit={(event) => void submit(event)}>
      <AvatarField id={agent.id} name={name.trim().length > 0 ? name : agent.name} value={avatar} onChange={setAvatar} />
      <label>
        {t.name}
        <input value={name} onChange={(event) => setName(event.target.value)} required />
      </label>
      {isGroup ? null : (
        <label>
          <span>
            {t.title} <span className="muted">{t.optional}</span>
          </span>
          <input value={title} onChange={(event) => setTitle(event.target.value)} placeholder={t.titlePlaceholder} title={t.titleHint} />
        </label>
      )}
      <label>
        {t.description}
        <textarea
          rows={6}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder={isGroup ? t.descriptionGroupPlaceholder : t.descriptionPlaceholder}
          title={isGroup ? t.descriptionGroupHint : t.descriptionHint}
        />
      </label>
      {isGroup || choices == null || choices.models.length === 0 ? null : <ModelField choices={choices} value={mainModel} onChange={setMainModel} />}
      {isGroup ? null : <EffortField value={effort} onChange={setEffort} />}
      {isGroup ? null : (
        <label className="range-field">
          <span>
            {t.context} <span className="muted">{t.contextTokens(`${Math.round(contextTokens / 1000)}k`)}</span>
          </span>
          <input type="range" min={CONTEXT_MIN} max={CONTEXT_MAX} step={5000} value={contextTokens} onChange={(event) => setContextTokens(Number(event.target.value))} title={t.contextHint} />
          <span className="range-ends muted">
            <span>{thousands(CONTEXT_MIN)}</span>
            <span>{thousands(CONTEXT_MAX)}</span>
          </span>
          {context != null ? (
            <span className="context-gauge" title={t.gaugeHint(context.inputTokens.toLocaleString(formatLocale()), context.outputTokens.toLocaleString(formatLocale()), context.windowTokens.toLocaleString(formatLocale()))}>
              <span className="context-bar" aria-hidden="true">
                <span className="context-fill" style={{ width: `${Math.min(100, ((context.inputTokens + context.outputTokens) / Math.max(1, contextTokens)) * 100)}%` }} />
              </span>
              <span className="muted">
                {context.inputTokens + context.outputTokens === 0 ? t.nothingSent : t.lastRequest(thousands(context.inputTokens), thousands(context.outputTokens), thousands(context.inputTokens + context.outputTokens), thousands(contextTokens))}
              </span>
            </span>
          ) : null}
        </label>
      )}
      {isGroup ? null : <CompactNow agentId={agent.id} busy={busy} />}
      <div className="settings-card">
        <div className="settings-card-text">
          <strong>{t.notifications}</strong>
          <span className="muted">{isGroup ? t.notifyGroup : t.notifyBot}</span>
        </div>
        <Switch checked={notify} onChange={setNotify} label={t.notifications} />
      </div>
      {error != null ? <div className="settings-error">{error}</div> : null}
      <div className="settings-actions">
        <button type="submit" className="primary" disabled={saving || name.trim().length === 0}>
          {t.save}
        </button>
        <button type="button" onClick={onClose} disabled={saving}>
          {t.cancel}
        </button>
        <span className="muted settings-note">{t.appliesNext}</span>
      </div>
    </form>
  );
}
